import React, { useMemo } from "react";

/* ============================================================================
   PROJECTSCOPEREPORT.JSX
   ----------------------------------------------------------------------------
   Module de synthèse globale du scope projet.
   Prévu pour :
   - lecture écran
   - préparation impression
   - futur export PDF

   SOURCES
   -------
   - projectIdentity
   - spatialEntities
   - buildingSystems
   - technicalSystems
   - specificAssets

   NOTE
   ----
   Le composant s’appuie volontairement sur :
   - HTML sémantique
   - classes CSS
   - main.css
============================================================================ */

const ProjectScopeReport = ({
  projectIdentity = {},
  spatialEntities = [],
  buildingSystems = [],
  technicalSystems = [],
  specificAssets = [],
}) => {
  const spatialSummary = useMemo(() => {
    const sites = spatialEntities.filter((item) => item.spatialType === "SITE");
    const levels = spatialEntities.filter((item) => item.spatialType === "LEVEL");
    const zones = spatialEntities.filter((item) => item.spatialType === "ZONE");

    const totalArea = spatialEntities.reduce(
      (sum, item) => sum + safeNumber(item.area),
      0
    );

    const totalPerimeter = spatialEntities.reduce(
      (sum, item) => sum + safeNumber(item.perimeter),
      0
    );

    return {
      sitesCount: sites.length,
      levelsCount: levels.length,
      zonesCount: zones.length,
      totalArea,
      totalPerimeter,
    };
  }, [spatialEntities]);

  const spatialRows = useMemo(() => {
    const sorted = [...spatialEntities].sort((a, b) => {
      const typeDelta = getSpatialTypeOrder(a.spatialType) - getSpatialTypeOrder(b.spatialType);
      if (typeDelta !== 0) return typeDelta;

      return String(a.name || "").localeCompare(String(b.name || ""), undefined, {
        sensitivity: "base",
        numeric: true,
      });
    });

    return sorted.map((item) => ({
      id: item.id,
      spatialType: item.spatialType || "",
      name: item.name || "",
      parentId: item.parentId || "—",
      elevation: formatValue(item.elevation),
      area: formatValue(item.area),
      perimeter: formatValue(item.perimeter),
    }));
  }, [spatialEntities]);

  const buildingRows = useMemo(() => {
    return buildLibraryRows(buildingSystems);
  }, [buildingSystems]);

  const technicalRows = useMemo(() => {
    return buildLibraryRows(technicalSystems);
  }, [technicalSystems]);

  const specificRows = useMemo(() => {
    return buildLibraryRows(specificAssets);
  }, [specificAssets]);

  const consolidated = useMemo(() => {
    return [
      buildConsolidatedRow("Building Systems", buildingSystems),
      buildConsolidatedRow("Technical Systems", technicalSystems),
      buildConsolidatedRow("Specific Assets", specificAssets),
    ];
  }, [buildingSystems, technicalSystems, specificAssets]);

  return (
    <div className="report-page">
      {/* ============================================================
         HEADER
      ============================================================ */}
      <section className="report-block">
        <div className="report-header">
          <div>
            <h2>Project Scope Summary</h2>
            <p className="muted">
              Consolidated project scope prepared for review and reporting.
            </p>
          </div>

          <div className="report-date-block">
            <div className="report-meta-label">Report Date</div>
            <div className="report-meta-value">{formatToday()}</div>
          </div>
        </div>

        <div className="page-separator" />

        <div className="report-meta-grid">
          <ReportMetaItem label="Project Name" value={projectIdentity.projectName} />
          <ReportMetaItem label="Reference" value={projectIdentity.reference} />
          <ReportMetaItem label="Client" value={projectIdentity.clientName} />
          <ReportMetaItem label="Location" value={projectIdentity.location} />
          <ReportMetaItem label="Project Type" value={projectIdentity.projectType} />
          <ReportMetaItem label="Country" value={projectIdentity.country} />
        </div>
      </section>

      {/* ============================================================
         SPATIAL SUMMARY
      ============================================================ */}
      <section className="report-block">
        <h3>Spatial Scope Summary</h3>

        <div className="report-kpi-grid">
          <ReportKpiCard label="Sites" value={spatialSummary.sitesCount} />
          <ReportKpiCard label="Levels" value={spatialSummary.levelsCount} />
          <ReportKpiCard label="Areas" value={spatialSummary.zonesCount} />
          <ReportKpiCard label="Total Area" value={formatValue(spatialSummary.totalArea)} />
          <ReportKpiCard
            label="Total Perimeter"
            value={formatValue(spatialSummary.totalPerimeter)}
          />
        </div>
      </section>

      {/* ============================================================
         SPATIAL TREE
      ============================================================ */}
      <section className="report-block">
        <h3>Spatial Structure</h3>

        <div className="report-table-wrapper">
          <table className="report-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Name</th>
                <th>Parent</th>
                <th>Elevation</th>
                <th>Area</th>
                <th>Perimeter</th>
              </tr>
            </thead>
            <tbody>
              {spatialRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="report-empty-cell">
                    No spatial data available.
                  </td>
                </tr>
              ) : (
                spatialRows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.spatialType}</td>
                    <td>{row.name}</td>
                    <td>{row.parentId}</td>
                    <td>{row.elevation}</td>
                    <td>{row.area}</td>
                    <td>{row.perimeter}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ============================================================
         BUILDING
      ============================================================ */}
      <section className="report-block">
        <h3>Building Systems</h3>
        <LibraryScopeTable rows={buildingRows} emptyMessage="No building systems configured." />
      </section>

      {/* ============================================================
         TECHNICAL
      ============================================================ */}
      <section className="report-block">
        <h3>Technical Systems</h3>
        <LibraryScopeTable rows={technicalRows} emptyMessage="No technical systems configured." />
      </section>

      {/* ============================================================
         SPECIFIC
      ============================================================ */}
      <section className="report-block">
        <h3>Specific Assets</h3>
        <LibraryScopeTable rows={specificRows} emptyMessage="No specific assets configured." />
      </section>

      {/* ============================================================
         CONSOLIDATED
      ============================================================ */}
      <section className="report-block">
        <h3>Consolidated Overview</h3>

        <div className="report-table-wrapper">
          <table className="report-table report-table-compact">
            <thead>
              <tr>
                <th>Library</th>
                <th>Total Items</th>
                <th>Active Items</th>
                <th>Assigned Items</th>
              </tr>
            </thead>
            <tbody>
              {consolidated.map((row) => (
                <tr key={row.library}>
                  <td>{row.library}</td>
                  <td>{row.totalItems}</td>
                  <td>{row.activeItems}</td>
                  <td>{row.assignedItems}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

/* ============================================================================
   SUBCOMPONENTS
============================================================================ */

const ReportMetaItem = ({ label, value }) => {
  return (
    <div className="report-meta-item">
      <div className="report-meta-label">{label}</div>
      <div className="report-meta-value">{value || "—"}</div>
    </div>
  );
};

const ReportKpiCard = ({ label, value }) => {
  return (
    <div className="report-kpi-card">
      <div className="report-kpi-label">{label}</div>
      <div className="report-kpi-value">{value}</div>
    </div>
  );
};

const LibraryScopeTable = ({ rows = [], emptyMessage = "No data." }) => {
  return (
    <div className="report-table-wrapper">
      <table className="report-table">
        <thead>
          <tr>
            <th>Family</th>
            <th>Title</th>
            <th>Status</th>
            <th>Unit</th>
            <th>Summary Label</th>
            <th>Summary Value</th>
            <th>Spatial Assignments</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={7} className="report-empty-cell">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.id}>
                <td>{row.family}</td>
                <td>{row.title}</td>
                <td>{row.status}</td>
                <td>{row.unit}</td>
                <td>{row.summaryLabel}</td>
                <td>{row.summaryValue}</td>
                <td>{row.spatialAssignments}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

/* ============================================================================
   HELPERS
============================================================================ */

function buildLibraryRows(items = []) {
  return [...items]
    .sort((a, b) =>
      String(a.title || "").localeCompare(String(b.title || ""), undefined, {
        sensitivity: "base",
        numeric: true,
      })
    )
    .map((item) => ({
      id: item.id,
      family: item.family || "—",
      title: item.title || "—",
      status: item.active ? "Active" : "Inactive",
      unit: item.unit || "—",
      summaryLabel: item.summaryLabel || "—",
      summaryValue: formatValue(item.summaryValue),
      spatialAssignments: formatSpatialAssignments(item.spatialAssignments),
    }));
}

function buildConsolidatedRow(library, items = []) {
  const totalItems = items.length;
  const activeItems = items.filter((item) => item.active).length;
  const assignedItems = items.filter(
    (item) => Array.isArray(item.spatialAssignments) && item.spatialAssignments.length > 0
  ).length;

  return {
    library,
    totalItems,
    activeItems,
    assignedItems,
  };
}

function formatSpatialAssignments(assignments) {
  if (!Array.isArray(assignments) || assignments.length === 0) return "—";

  return assignments
    .map((item) => item.spatialType || item.spatialEntityId || "—")
    .join(", ");
}

function safeNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function formatValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

function getSpatialTypeOrder(type) {
  switch (type) {
    case "SITE":
      return 1;
    case "LEVEL":
      return 2;
    case "ZONE":
      return 3;
    default:
      return 999;
  }
}

function formatToday() {
  const date = new Date();
  return date.toLocaleDateString();
}

export default ProjectScopeReport;